import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useRole, UserRole } from "../../context/RoleContext";

interface ProtectedRouteProps {
  allowedRoles: UserRole[];
  children?: React.ReactNode; // To allow wrapping <Route element={<Component />} />
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  children,
}) => {
  const { userRole } = useRole();

  if (userRole === "UNKNOWN") {
    // While role is loading/unknown, show a lightweight guard to prevent premature render
    return (
      <div className="flex items-center justify-center h-full p-6">
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
      </div>
    );
  }

  const isAllowed = allowedRoles.includes(userRole);

  if (!isAllowed) {
    console.warn(
      `ProtectedRoute: Access denied for role "${userRole}". Allowed roles: ${allowedRoles.join(
        ", "
      )}. Redirecting.`
    );
    // Redirect to a generic page, or an "Access Denied" page
    return <Navigate to="/" replace />;
  }

  // If children are provided (for wrapping a Route's element), render them.
  // Otherwise, render an <Outlet /> for nested routes if this component is used as a layout route.
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
